import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
    createTracker,
    createTrackerEntry,
    deleteTracker,
    fetchTrackerById,
    fetchTrackerEntries,
    fetchTrackerScore,
    fetchTrackersOfUser,
    updateTracker,
} from '../api/trackerApi';

export function useTracker(token, id) {
    return useQuery({
        queryKey: ['tracker', id],
        queryFn: () => fetchTrackerById(token, id),
        enabled: !!token && !!id,
    });
}

export function useTrackers(token) {
    return useQuery({
        queryKey: ['trackers'],
        queryFn: () => fetchTrackersOfUser(token),
        enabled: !!token,
    });
}

export function useTrackerEntries(token, trackerId) {
    return useQuery({
        queryKey: ['tracker-entries', trackerId],
        queryFn: () => fetchTrackerEntries(token, trackerId),
        enabled: !!token && !!trackerId,
    });
}

export function useCreateTracker(token) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (trackerData) => createTracker(token, trackerData),
        onSuccess: () => {
            queryClient.invalidateQueries(['trackers']);
            queryClient.invalidateQueries(['stats']);
        },
    });
}

export function useUpdateTracker(token, id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (trackerData) => updateTracker(token, id, trackerData),
        onSuccess: () => {
            queryClient.invalidateQueries(['tracker', id]);
            queryClient.invalidateQueries(['trackers']);
            queryClient.invalidateQueries(['stats']);
        },
    });
}

export function useDeleteTracker(token) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (trackerId) => deleteTracker(token, trackerId),
        onSuccess: () => {
            queryClient.invalidateQueries(['trackers']);
            queryClient.invalidateQueries(['stats']);
        },
    });
}

export function useTrackerScore(token, date, timezone) {
    return useQuery({
        queryKey: ['tracker-score', date],
        queryFn: () => fetchTrackerScore(token, date, timezone),
        enabled: !!token && !!date,
        staleTime: 0,
    });
}

export function useCreateTrackerEntry(token, trackerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (entryData) => createTrackerEntry(token, trackerId, entryData),
        onSuccess: () => {
            queryClient.invalidateQueries(['tracker-entries', trackerId]);
            queryClient.invalidateQueries(['tracker', trackerId]);
            queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'tracker-score' });
            queryClient.invalidateQueries(['stats']);
        },
    });
}
